/**
 * Pure display formatters for this package's components. Kept self-contained
 * (no store / app imports) so the components that use them stay store-agnostic
 * and portable. The app has its own copy of the common ones in
 * `mobile/utils/format.js`; these are the subset the extracted components need.
 *
 * `avatarGradient` and `formatPrice` are RE-EXPORTED from `-shared`, not
 * redeclared here. They went with the messaging components that moved down a
 * tier, and a mobile-only component still needs them — `SaleCard` and `LotCard`
 * price things, `NotificationBanner` draws an avatar. Re-exporting keeps every
 * existing `import { formatPrice } from '../../utils/formatters'` working while
 * there stays exactly one copy of each function in the monorepo.
 */

export { AVATAR_GRADIENTS, avatarGradient, formatPrice, initialsOf } from '@hds/auto-auction-com-components-shared/utils/formatters.js';

/**
 * Compact remaining-time label from a seconds count: `45s`, `12m 05s`, `2h 59m`.
 * Sub-minute values keep the bare `Ns` form (a lot seconds-from-hammer); longer
 * timed-auction windows collapse to m/s or h/m so a multi-hour countdown stays
 * readable instead of showing thousands of raw seconds.
 */
export const formatCountdown = (secs) => {
  const s = Math.max(0, Math.floor(secs));
  if (s >= 3600) return `${Math.floor(s / 3600)}h ${Math.floor((s % 3600) / 60)}m`;
  if (s >= 60) return `${Math.floor(s / 60)}m ${String(s % 60).padStart(2, '0')}s`;
  return `${s}s`;
};

/**
 * A range-track figure for a readout: compact when it is a whole thousand, the
 * full number otherwise — `30000` → `30k`, `20500` → `20500`, `400` → `400`.
 *
 * The compact reading is what the design asks for and what a dealer takes in at
 * a glance, and rounding to the nearest thousand is how RangeSliderScreen used
 * to get it. That made the sentence on screen able to disagree with the value
 * the screen then reported: a mileage ceiling of 400 read "Under 0k mi", and
 * two different price drafts read identically.
 *
 * Compacting only where the compaction is EXACT is what makes the readout and
 * the payload agree by construction, rather than by the caller happening to
 * choose a `step` that lands on a thousand. That property is the whole reason
 * this is a formatter with a test rather than an arrow function in a template:
 * the boundary — a thousand, and one either side of it — is the thing worth
 * holding, and through the DOM it can only be reached by mounting a screen and
 * reading a sentence.
 *
 * Anything that is not a finite number is handed back as its own string rather
 * than compacted, because a readout showing `NaNk` says less than one showing
 * `NaN`.
 */
export const formatThousands = (value) =>
  Number.isFinite(value) && value % 1000 === 0 ? `${value / 1000}k` : String(value);

const WD_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MO_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const startOfDayMs = (ms) => {
  const d = new Date(ms);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
};

/**
 * Format an absolute start (ms) as e.g. `Wed, Jun 25 · 10:00 AM`, collapsing the
 * near term to `Today` / `Tomorrow`. Returns null for running lots (no start).
 * `now` is injectable so the caller can pass a frozen/demo clock; defaults to
 * a fresh `new Date()`.
 */
export const formatSaleStart = (
  ms,
  now = new Date()
) => {
  if (ms == null) return null;
  const d = new Date(ms);
  let h = d.getHours();
  const ampm = h >= 12 ? 'PM' : 'AM';
  h = h % 12 || 12;
  const time = `${h}:${String(d.getMinutes()).padStart(2, '0')} ${ampm}`;
  const dayDelta = Math.round((startOfDayMs(ms) - startOfDayMs(now.getTime())) / 86400000);
  const day =
    dayDelta === 0 ? 'Today'
    : dayDelta === 1 ? 'Tomorrow'
    : `${WD_SHORT[d.getDay()]}, ${MO_SHORT[d.getMonth()]} ${d.getDate()}`;
  return `${day} · ${time}`;
};

/**
 * The industry-standard auction light codes, and the color each one names.
 *
 * One copy, read by the three components that draw a light: `AuctionLights`
 * paints the pip row, and `FilterRow` / `SelectionListScreen` each paint a
 * single labeled dot. Three inline copies of this list is what it replaced.
 *
 * Full color names rather than the raw codes, because these become BEM
 * modifiers and therefore token segments, and `--p-…-auction-light-red-background`
 * reads where `-r-` does not. The feed sends codes in either case, which is
 * what `auctionLightName` is for.
 */
export const AUCTION_LIGHT_NAMES = Object.freeze({
  r: 'red',
  g: 'green',
  y: 'yellow',
  b: 'blue',
  w: 'white',
});

/**
 * A feed's light code as its full color name, or null if it is not a code.
 *
 * Codes arrive unvalidated and in either case, so normalizing here keeps that
 * off every call site — the same reason `AuctionLights` has always upper-cased
 * and filtered its own `lights` prop rather than trusting the caller.
 *
 * The FULL NAME rather than the code, because this is what a class modifier and
 * therefore a token segment are built from, and `--p-mobile-filter-row-auction-light-red-background`
 * says what `-r-` does not.
 */
export const auctionLightName = (code) => AUCTION_LIGHT_NAMES[String(code ?? '').toLowerCase()] ?? null;

/**
 * The same five codes, upper case, as the feed sends them.
 *
 * The one set a prop validator reads. It exists because there were two: this
 * file's five-code map, and a four-code `AUCTION_LIGHTS` beside BidCard with no
 * white in it — which rejected every asset in the app's own catalog, since all
 * nineteen carry `W`. That never fired only because the app filtered white out
 * before the card ever saw it, so the contradiction sat one call site away from
 * warning on every row.
 *
 * Whether a code DRAWS is a separate question and stays each component's own:
 * BidCard treats white as the absence of a light and paints no pip for it, and
 * the bid tray paints all five. A validator that encoded either answer would
 * make one component's drawing rule into the other's contract.
 */
export const AUCTION_LIGHT_CODES = Object.freeze(
  Object.keys(AUCTION_LIGHT_NAMES).map((code) => code.toUpperCase())
);
